import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, withModifiers as _withModifiers, unref as _unref, vShow as _vShow, withDirectives as _withDirectives, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-26ce2ef2"),n=n(),_popScopeId(),n)
const _hoisted_1 = {
  class: "menu is-paddingless",
  "data-unit": "toc-3d-content"
}
const _hoisted_2 = {
  key: 0,
  class: "buttons has-addons toc-expand-collapse"
}
const _hoisted_3 = { class: "menu-list" }
const _hoisted_4 = { class: "toc-row toc-model-row" }
const _hoisted_5 = ["aria-expanded", "aria-label", "onClick"]
const _hoisted_6 = { class: "icon is-small" }
const _hoisted_7 = ["onClick"]
const _hoisted_8 = { class: "toc-item-text" }
const _hoisted_9 = ["innerHTML"]
const _hoisted_10 = { key: 1 }
const _hoisted_11 = ["innerHTML"]
const _hoisted_12 = {
  key: 3,
  class: "help-text"
}
const _hoisted_13 = ["onClick"]
const _hoisted_14 = { class: "toc-item-text" }
const _hoisted_15 = ["innerHTML"]
const _hoisted_16 = { key: 1 }
const _hoisted_17 = ["innerHTML"]
const _hoisted_18 = {
  key: 3,
  class: "help-text"
}
const _hoisted_19 = {
  key: 1,
  "aria-live": "polite",
  class: "has-text-grey has-text-centered search-status",
  role: "status"
}

import { computed, type PropType } from 'vue'
import { useStore } from 'vuex'
import i18n from '@/locales'
import { sanitizeHtml } from '@/helpers/sanitizeHtml'
import {
  getThreeDHighlightedContent,
  getThreeDTocSearchResultKey
} from '@/helpers/threeDTocSearch'
import type { ThreeDTocSearchResult } from '@/interfaces/library'

type SearchableSnapshot = Record<string, unknown> & {
  id: number
  searchResult?: ThreeDTocSearchResult | null
}

type SearchableModel = Record<string, unknown> & {
  id: number
  name: string
  searchResult?: ThreeDTocSearchResult | null
  snapshots: SearchableSnapshot[]
}


export default /*#__PURE__*/_defineComponent({
  __name: 'TocThreeDContent',
  props: {
  beforeSelect: {
    default: undefined,
    type: Function as PropType<() => Promise<void> | void>
  }
},
  emits: ['select'],
  setup(__props, { emit: __emit }) {

const store = useStore()
const emit = __emit
const props = __props
const models = computed<SearchableModel[]>(() => store.getters['content/threeDModels'])
const expandedModelIds = computed(() => store.state.content.toc.expandedThreeDModelIds)
const selectedModelId = computed(() => store.state.content.toc.selectedThreeDModelId)
const selectedSearchResultKey = computed(() =>
  store.state.content.toc.selectedThreeDSearchResultKey || ''
)
const selectedSnapshotId = computed(() => store.state.content.toc.selectedThreeDSnapshotId)
const searchResults = computed<ThreeDTocSearchResult[]>(() =>
  store.state.content.toc.searchResults ?? []
)
const tocQuery = computed(() => {
  const query = store.state.content.toc.query
  return typeof query === 'string' ? query : ''
})
const hasQuery = computed(() => Boolean(tocQuery.value.trim()))
const locale = computed(() => {
  const currentLocale = i18n.global.locale
  return typeof currentLocale === 'object' && 'value' in currentLocale
    ? currentLocale.value
    : currentLocale
})
const resultsByKey = computed(() => new Map(
  searchResults.value
    .map(result => [getThreeDTocSearchResultKey(result), result] as const)
    .filter(([key]) => Boolean(key))
))
const filteredModels = computed<SearchableModel[]>(() => {
  if (!hasQuery.value) return models.value

  return models.value.reduce<SearchableModel[]>((filtered, model) => {
    const modelResult = resultsByKey.value.get(`model:${String(model.id)}`) || null
    const snapshots = model.snapshots.reduce<SearchableSnapshot[]>((matches, snapshot) => {
      const searchResult = resultsByKey.value.get(`snapshot:${String(snapshot.id)}`) || null
      if (modelResult || searchResult) matches.push({ ...snapshot, searchResult })
      return matches
    }, [])

    if (modelResult || snapshots.length) {
      filtered.push({ ...model, searchResult: modelResult, snapshots })
    }
    return filtered
  }, [])
})

const isExpanded = (modelId: number) => hasQuery.value || expandedModelIds.value.includes(modelId)
const isSearchResultActive = (result?: ThreeDTocSearchResult | null) => {
  const key = getThreeDTocSearchResultKey(result)
  return Boolean(key) && key === selectedSearchResultKey.value
}
const isModelActive = (model: SearchableModel) => selectedSearchResultKey.value
  ? isSearchResultActive(model.searchResult)
  : model.id === selectedModelId.value
const isSnapshotActive = (snapshot: SearchableSnapshot) => selectedSearchResultKey.value
  ? isSearchResultActive(snapshot.searchResult)
  : snapshot.id === selectedSnapshotId.value
const getPresentation = (result?: ThreeDTocSearchResult | null) =>
  getThreeDHighlightedContent(result, locale.value)
const selectModel = async (model: SearchableModel) => {
  await props.beforeSelect?.()
  if (model.searchResult) {
    await store.dispatch('content/selectThreeDTocSearchResult', {
      searchResult: model.searchResult
    })
  } else {
    await store.dispatch('content/selectThreeDModel', model.id)
  }
  emit('select')
}
const selectSnapshot = async (snapshot: SearchableSnapshot) => {
  await props.beforeSelect?.()
  if (snapshot.searchResult) {
    await store.dispatch('content/selectThreeDTocSearchResult', {
      searchResult: snapshot.searchResult
    })
  } else {
    await store.dispatch('content/selectThreeDSnapshot', snapshot)
  }
  emit('select')
}
const toggleExpanded = (modelId: number) => {
  if (!hasQuery.value) store.dispatch('content/toggleThreeDModelExpanded', modelId)
}
const setAllExpanded = (expanded: boolean) => store.dispatch('content/setAllThreeDModelsExpanded', expanded)

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("aside", _hoisted_1, [
    (!hasQuery.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createElementVNode("button", {
            class: "button",
            type: "button",
            onClick: _cache[0] || (_cache[0] = ($event: any) => (setAllExpanded(true)))
          }, _toDisplayString(_ctx.$t('expandAll')), 1),
          _createElementVNode("button", {
            class: "button",
            type: "button",
            onClick: _cache[1] || (_cache[1] = ($event: any) => (setAllExpanded(false)))
          }, _toDisplayString(_ctx.$t('collapseAll')), 1)
        ]))
      : _createCommentVNode("", true),
    _createElementVNode("ul", _hoisted_3, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredModels.value, (model) => {
        return (_openBlock(), _createElementBlock("li", {
          key: model.id,
          "data-unit": "toc-3d-model"
        }, [
          _createElementVNode("div", _hoisted_4, [
            _createElementVNode("button", {
              class: "button is-white expand-toggle",
              type: "button",
              "aria-expanded": isExpanded(model.id),
              "aria-label": isExpanded(model.id) ? _ctx.$t('collapse') : _ctx.$t('expand'),
              onClick: _withModifiers(($event: any) => (toggleExpanded(model.id)), ["stop"])
            }, [
              _createElementVNode("span", _hoisted_6, [
                _createElementVNode("i", {
                  "aria-hidden": "true",
                  class: _normalizeClass(isExpanded(model.id) ? 'fas fa-chevron-down' : 'fas fa-chevron-right')
                }, null, 2)
              ])
            ], 8, _hoisted_5),
            _createElementVNode("button", {
              class: _normalizeClass(["button is-white toc-label", { 'is-active': isModelActive(model) }]),
              type: "button",
              onClick: ($event: any) => (selectModel(model))
            }, [
              _createElementVNode("span", _hoisted_8, [
                (getPresentation(model.searchResult).highlightedNameHtml)
                  ? (_openBlock(), _createElementBlock("span", {
                      key: 0,
                      class: "toc-item-highlight",
                      innerHTML: _unref(sanitizeHtml)(getPresentation(model.searchResult).highlightedNameHtml || '')
                    }, null, 8, _hoisted_9))
                  : (_openBlock(), _createElementBlock("span", _hoisted_10, _toDisplayString(model.name), 1)),
                (getPresentation(model.searchResult).helpTextHtml)
                  ? (_openBlock(), _createElementBlock("span", {
                      key: 2,
                      class: "help-text toc-item-highlight",
                      innerHTML: _unref(sanitizeHtml)(getPresentation(model.searchResult).helpTextHtml || '')
                    }, null, 8, _hoisted_11))
                  : (getPresentation(model.searchResult).helpTextKey)
                    ? (_openBlock(), _createElementBlock("span", _hoisted_12, _toDisplayString(_ctx.$tc(
                  getPresentation(model.searchResult).helpTextKey,
                  getPresentation(model.searchResult).helpTextParams?.count || 1,
                  getPresentation(model.searchResult).helpTextParams
                )), 1))
                    : _createCommentVNode("", true)
              ])
            ], 10, _hoisted_7)
          ]),
          _withDirectives(_createElementVNode("ul", null, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(model.snapshots, (snapshot) => {
              return (_openBlock(), _createElementBlock("li", {
                key: snapshot.id
              }, [
                _createElementVNode("button", {
                  class: _normalizeClass(["button is-white toc-row snapshot-row", { 'is-active': isSnapshotActive(snapshot) }]),
                  type: "button",
                  onClick: ($event: any) => (selectSnapshot(snapshot))
                }, [
                  _createElementVNode("span", _hoisted_14, [
                    (getPresentation(snapshot.searchResult).highlightedNameHtml)
                      ? (_openBlock(), _createElementBlock("span", {
                          key: 0,
                          class: "toc-item-highlight",
                          innerHTML: _unref(sanitizeHtml)(getPresentation(snapshot.searchResult).highlightedNameHtml || '')
                        }, null, 8, _hoisted_15))
                      : (_openBlock(), _createElementBlock("span", _hoisted_16, _toDisplayString(snapshot.snapshotName || snapshot.id), 1)),
                    (getPresentation(snapshot.searchResult).helpTextHtml)
                      ? (_openBlock(), _createElementBlock("span", {
                          key: 2,
                          class: "help-text toc-item-highlight",
                          innerHTML: _unref(sanitizeHtml)(getPresentation(snapshot.searchResult).helpTextHtml || '')
                        }, null, 8, _hoisted_17))
                      : (getPresentation(snapshot.searchResult).helpTextKey)
                        ? (_openBlock(), _createElementBlock("span", _hoisted_18, _toDisplayString(_ctx.$tc(
                    getPresentation(snapshot.searchResult).helpTextKey,
                    getPresentation(snapshot.searchResult).helpTextParams?.count || 1,
                    getPresentation(snapshot.searchResult).helpTextParams
                  )), 1))
                        : _createCommentVNode("", true)
                  ])
                ], 10, _hoisted_13)
              ]))
            }), 128))
          ], 512), [
            [_vShow, isExpanded(model.id)]
          ])
        ]))
      }), 128))
    ]),
    (hasQuery.value && !filteredModels.value.length)
      ? (_openBlock(), _createElementBlock("p", _hoisted_19, _toDisplayString(_ctx.$t('noSearchResultsHelpText')), 1))
      : _createCommentVNode("", true)
  ]))
}
}

})